import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Copy, ExternalLink, QrCode, Globe, FileText, Calendar, User, LogIn, LayoutDashboard, X } from 'lucide-react';

/**
 * Reusable Live URLs panel — shows public + private tenant URLs with Copy / Open / QR buttons.
 * Computes base URL as the current production origin so it works on both preview and production.
 */
export default function LiveURLsPanel({ tenant }) {
  const [qrOpen, setQrOpen] = useState(null);

  const base = useMemo(() => {
    // Prefer the production domain if we are on it, else fall back to current origin
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname;
    if (host.endsWith('depinacrm.com') || host === 'depinacrm.com') return 'https://depinacrm.com';
    return window.location.origin;
  }, []);

  if (!tenant?.slug) return null;

  const slug = tenant.slug;
  const isDaycare = (tenant.template_slug || '').toLowerCase() === 'daycare'
    || (tenant.vertical || '').toUpperCase() === 'DAYCARE';

  const items = [
    isDaycare && { key: 'website',  label: 'Public Website',   icon: Globe,        url: `${base}/${slug}`,           public: true,
      hint: 'Marketing homepage with embedded enrollment form.' },
    isDaycare && { key: 'enroll',   label: 'Enrollment Form',  icon: FileText,     url: `${base}/${slug}/enroll`,    public: true,
      hint: 'Share on Care.com, Facebook, IG, business cards.' },
    isDaycare && { key: 'tour',     label: 'Tour Booking',     icon: Calendar,     url: `${base}/${slug}/tour`,      public: true,
      hint: 'Schedule a tour page (creates tour_scheduled lead).' },
    isDaycare && { key: 'parent',   label: 'Parent Portal',    icon: User,         url: `${base}/${slug}/parent`,    public: false,
      hint: 'Parents log in here to see their child.' },
    { key: 'login',     label: 'Admin Login',      icon: LogIn,        url: `${base}/${slug}/login`,     public: false,
      hint: 'Owner & staff login.' },
    { key: 'crm',       label: 'CRM Dashboard',    icon: LayoutDashboard, url: `${base}/${slug}/dashboard`, public: false,
      hint: 'Admin CRM (after login).' },
  ].filter(Boolean);

  const copy = (url, label) => {
    navigator.clipboard.writeText(url).then(() => toast.success(`${label} URL copied`));
  };

  const copyAll = () => {
    const lines = items.map(i => `${i.label}: ${i.url}`).join('\n');
    navigator.clipboard.writeText(lines).then(() => toast.success('All URLs copied'));
  };

  return (
    <section className="detail-section" data-testid="live-urls-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h4 style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <Globe size={16} /> Live URLs
        </h4>
        <button onClick={copyAll} data-testid="live-urls-copy-all" style={btn}>
          <Copy size={12} /> Copy all
        </button>
      </div>
      <p style={{ color: '#64748b', fontSize: 12, marginTop: 0, marginBottom: 14 }}>
        Share <strong>Public</strong> URLs anywhere. <strong>Private</strong> URLs are for the owner/staff.
      </p>

      <div style={{ display: 'grid', gap: 10 }}>
        {items.map(item => {
          const Icon = item.icon;
          return (
            <div key={item.key} className="live-url-row" data-testid={`live-url-${item.key}`} style={row}>
              <div style={iconBox(item.public)}>
                <Icon size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong style={{ fontSize: 13, color: '#0f172a' }}>{item.label}</strong>
                  <span style={badge(item.public)}>{item.public ? 'Public' : 'Private'}</span>
                </div>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, color: '#2563eb', wordBreak: 'break-all', textDecoration: 'none' }}
                >
                  {item.url}
                </a>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>{item.hint}</p>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button title="Copy URL" onClick={() => copy(item.url, item.label)} data-testid={`copy-${item.key}`} style={iconBtn}>
                  <Copy size={14} />
                </button>
                <a title="Open" href={item.url} target="_blank" rel="noreferrer" data-testid={`open-${item.key}`} style={{ ...iconBtn, textDecoration: 'none' }}>
                  <ExternalLink size={14} />
                </a>
                {item.public && (
                  <button title="QR Code" onClick={() => setQrOpen(item)} data-testid={`qr-${item.key}`} style={iconBtn}>
                    <QrCode size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {qrOpen && (
        <div className="slide-over-overlay" onClick={() => setQrOpen(null)} style={{ zIndex: 1200 }}>
          <div onClick={e => e.stopPropagation()} style={qrModal} data-testid="qr-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <strong style={{ fontSize: 15 }}>{qrOpen.label} — QR Code</strong>
              <button onClick={() => setQrOpen(null)} style={{ ...iconBtn, padding: 6 }}><X size={16} /></button>
            </div>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(qrOpen.url)}`}
              alt={`QR for ${qrOpen.label}`}
              style={{ width: 320, height: 320, display: 'block', margin: '0 auto', borderRadius: 12, border: '1px solid #e2e8f0' }}
              data-testid="qr-image"
            />
            <p style={{ textAlign: 'center', fontSize: 12, color: '#64748b', marginTop: 12, wordBreak: 'break-all' }}>{qrOpen.url}</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
              <button onClick={() => copy(qrOpen.url, qrOpen.label)} style={{ ...btn, padding: '8px 14px' }}>
                <Copy size={12} /> Copy URL
              </button>
              <a
                href={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(qrOpen.url)}`}
                download={`${tenant.slug}-${qrOpen.key}-qr.png`}
                target="_blank"
                rel="noreferrer"
                style={{ ...btn, padding: '8px 14px', textDecoration: 'none' }}
                data-testid="qr-download"
              >
                <QrCode size={12} /> Download PNG
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: '#fff', color: '#0f172a',
  padding: '6px 12px', borderRadius: 8,
  fontSize: 12, fontWeight: 600,
  border: '1px solid #e2e8f0', cursor: 'pointer',
};

const iconBtn = {
  background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: 8, cursor: 'pointer',
  display: 'grid', placeItems: 'center', color: '#475569',
};

const row = {
  display: 'flex', gap: 12, alignItems: 'flex-start',
  padding: 12, background: '#f8fafc',
  border: '1px solid #e2e8f0', borderRadius: 10,
};

const iconBox = (isPublic) => ({
  width: 36, height: 36, borderRadius: 10,
  background: isPublic ? '#dcfce7' : '#dbeafe',
  color: isPublic ? '#16a34a' : '#2563eb',
  display: 'grid', placeItems: 'center', flexShrink: 0,
});

const badge = (isPublic) => ({
  background: isPublic ? '#dcfce7' : '#e2e8f0',
  color: isPublic ? '#16a34a' : '#475569',
  fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
});

const qrModal = {
  background: '#fff', borderRadius: 16, padding: 20,
  maxWidth: 380, margin: '10vh auto 0', position: 'relative',
};
